import { barY, defineChart } from "@tanstack/charts/universal"
import {
  annotationsOf,
  backdropsOf,
  dailySpecOf,
  dayAxis,
  daySpanScale,
  dayPickingOf,
  pickedOf,
  valueAxis,
  valuesOf,
  type DailyValue,
} from "./daily.ts"
import type { DailyChartInput } from "./input.ts"

/** A series' value on a day its history is known, as a bar stacks it. */
export type DailyBar<K extends string> = DailyValue<K> & { value: number }

/** How strongly the days not picked are drawn while a day is: they recede behind the pick. */
const receded = 0.4

/**
 * Each UTC day's series stacked into one bar, the first series on the baseline. A day whose
 * history isn't known draws no bar and stands in a muted band, so it never reads as a day of
 * nothing. An annotated day carries a dashed line, its label standing above the plot. While a day
 * on the chart is picked, every other day's bar recedes.
 */
export function dailyBars<K extends string>(input: DailyChartInput<K>) {
  const bars = valuesOf(input).filter((bar): bar is DailyBar<K> => bar.value !== null)
  const totals = new Map<string, number>()
  for (const { day, value } of bars) totals.set(day, (totals.get(day) ?? 0) + value)
  const y = valueAxis(Math.max(0, ...totals.values()), input)
  const picked = pickedOf(input)
  const stack = (id: string, of: readonly DailyBar<K>[], fillOpacity?: number) =>
    barY(of, {
      id,
      x: "day",
      y: "value",
      z: (bar) => bar.series,
      color: (bar) => bar.series,
      layout: { type: "stack", order: input.series.map(({ key }) => key) },
      radius: { end: 3 },
      // A short range's days stay bars rather than blocks.
      maxThickness: 48,
      ...(fillOpacity !== undefined && { fillOpacity }),
    })
  return defineChart({
    marks: [
      ...backdropsOf(input, y.top),
      // Each day's segments all stand in one of the two, so every day still stacks whole.
      stack(
        "bars",
        bars.filter(({ day }) => !picked || day === picked),
      ),
      stack(
        "receded-bars",
        bars.filter(({ day }) => picked && day !== picked),
        receded,
      ),
      ...annotationsOf(input, y.top),
    ],
    scales: { x: dayAxis(input), y: y.options, ...daySpanScale(input) },
    ...dailySpecOf(input),
    ...dayPickingOf(input),
  })
}
